import {
  SITE_EMAIL,
  SITE_LEGAL_NAME,
  SITE_LOGO_URL,
  SITE_NAME,
  SITE_OGRN,
  SITE_PHONE,
  SITE_RETURN_POLICY_URL,
  SITE_SOCIAL_PROFILES,
  SITE_URL
} from '@constants/site';
import type { JsonLdDocument, JsonLdNode } from './types';
import { SCHEMA_CONTEXT } from './types';
import { WEEK_DAYS } from './weekdays';

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;

export const RETURN_POLICY_ID = `${SITE_URL}/#return-policy`;

export const RETURN_WINDOW_DAYS = 3;

export const SHIPPING_SERVICE_ID = `${SITE_URL}/#shipping`;

export const SHIPPING_RATE_RUB = 49;

export const FREE_SHIPPING_FROM_RUB = 5000;

const SAME_OR_NEXT_DAY: JsonLdNode = {
  '@type': 'QuantitativeValue',
  minValue: 0,
  maxValue: 1,
  unitCode: 'DAY'
};

const RUSSIA: JsonLdNode = { '@type': 'DefinedRegion', addressCountry: 'RU' };

const shippingCondition = (
  orderValue: JsonLdNode,
  rate: number
): JsonLdNode => ({
  '@type': 'ShippingConditions',
  shippingDestination: RUSSIA,
  orderValue,
  shippingRate: { '@type': 'MonetaryAmount', value: rate, currency: 'RUB' },
  transitTime: {
    '@type': 'ServicePeriod',
    duration: SAME_OR_NEXT_DAY,
    businessDays: [...WEEK_DAYS]
  }
});

export const buildShippingService = (): JsonLdNode => ({
  '@type': 'ShippingService',
  '@id': SHIPPING_SERVICE_ID,
  name: 'Курьерская доставка по Москве и Московской области',
  description:
    'Москва и Московская область в пределах 30 км от МКАД, интервал от 3 часов, минимальный заказ 1000 ₽',
  fulfillmentType: 'FulfillmentTypeDelivery',
  handlingTime: {
    '@type': 'ServicePeriod',
    duration: SAME_OR_NEXT_DAY,
    businessDays: [...WEEK_DAYS]
  },
  shippingConditions: [
    shippingCondition(
      {
        '@type': 'MonetaryAmount',
        minValue: 0,
        maxValue: FREE_SHIPPING_FROM_RUB - 0.01,
        currency: 'RUB'
      },
      SHIPPING_RATE_RUB
    ),
    shippingCondition(
      {
        '@type': 'MonetaryAmount',
        minValue: FREE_SHIPPING_FROM_RUB,
        currency: 'RUB'
      },
      0
    )
  ]
});

export const buildShippingDetails = (): JsonLdNode => ({
  '@type': 'OfferShippingDetails',
  shippingRate: {
    '@type': 'MonetaryAmount',
    value: SHIPPING_RATE_RUB,
    currency: 'RUB'
  },
  shippingDestination: RUSSIA,
  deliveryTime: {
    '@type': 'ShippingDeliveryTime',
    handlingTime: SAME_OR_NEXT_DAY,
    transitTime: SAME_OR_NEXT_DAY
  },
  hasShippingService: { '@id': SHIPPING_SERVICE_ID }
});

export const buildReturnPolicy = (): JsonLdNode => ({
  '@type': 'MerchantReturnPolicy',
  '@id': RETURN_POLICY_ID,
  applicableCountry: 'RU',
  returnPolicyCountry: 'RU',
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: RETURN_WINDOW_DAYS,
  returnFees: 'https://schema.org/FreeReturn',
  refundType: [
    'https://schema.org/FullRefund',
    'https://schema.org/ExchangeRefund'
  ],
  merchantReturnLink: SITE_RETURN_POLICY_URL
});

export const organizationRef = (): JsonLdNode => ({
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: SITE_NAME,
  url: SITE_URL,
  logo: SITE_LOGO_URL
});

export const buildOrganization = (): JsonLdDocument => ({
  '@context': SCHEMA_CONTEXT,
  '@type': ['Organization', 'OnlineStore'],
  '@id': ORGANIZATION_ID,
  name: SITE_NAME,
  legalName: SITE_LEGAL_NAME,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: SITE_LOGO_URL,
    width: 576,
    height: 120
  },
  image: SITE_LOGO_URL,
  telephone: SITE_PHONE,
  email: SITE_EMAIL,
  address: {
    '@type': 'PostalAddress',
    postalCode: '140053',
    addressRegion: 'Московская область',
    addressLocality: 'Котельники',
    streetAddress: 'мкр Силикат, строение 4, пом. 2/6',
    addressCountry: 'RU'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    telephone: SITE_PHONE,
    email: SITE_EMAIL,
    availableLanguage: 'ru',
    hoursAvailable: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [...WEEK_DAYS],
      opens: '06:00',
      closes: '00:00'
    }
  },
  identifier: {
    '@type': 'PropertyValue',
    propertyID: 'ОГРН',
    value: SITE_OGRN
  },
  sameAs: SITE_SOCIAL_PROFILES,
  hasMerchantReturnPolicy: buildReturnPolicy(),
  hasShippingService: buildShippingService()
});
